import { ONE_DAY_IN_MILLISECONDS } from '../hooks/rentalExtension';
import { APP_STATE } from '../webStorage';
import { getPersistenceEnabled, setPersistenceEnabled } from './cookies';

/**
 * Checks whether the WebApp client is running in development mode. Both localhost and .dev domains are considered development.
 * @returns True if the app runs in dev mode, false otherwise (UAT, PROD, etc.).
 */
export const isDevEnv = () => {
  if (typeof window === 'undefined' || !window.location) {
    return false;
  }

  return ['localhost', '127.0.0.1'].includes(window.location.hostname) || window.location.hostname.includes('.dev.');
};

export const disablePersistenceAndClearStateFromStorage = session => {
  setPersistenceEnabled(session, false);
  localStorage.removeItem(APP_STATE);
};

export const enablePersistence = session => {
  setPersistenceEnabled(session, true);
};

const canPersist = session => getPersistenceEnabled(session);

/**
 * Persists the given state to localStorage if the session allows persistence.
 *
 * The state is first JSON-stringified, then encoded as a Uint8Array using TextEncoder,
 * then converted to a base64 string. The payload includes the encoded state and an
 * expiration timestamp set to one day from the current time.
 *
 * @param {Object} session - The current session object used to determine if persistence is allowed.
 * @param {Object} state - The application state to be persisted.
 */
export const persistStore = (session, state) => {
  if (!canPersist(session)) {
    return;
  }

  try {
    const encodedState = new TextEncoder().encode(JSON.stringify(state));
    const base64State = btoa(encodedState.reduce((data, byte) => data + String.fromCharCode(byte), ''));
    const payload = {
      data: base64State,
      expiresAt: Date.now() + ONE_DAY_IN_MILLISECONDS,
    };

    localStorage.setItem(APP_STATE, JSON.stringify(payload));
  } catch (err) {
    console.warn('Failed to persist Redux state:', err);
  }
};

/**
 * Decodes and retrieves a persisted state object from a base64-encoded payload.
 *
 * @param {string} stored - The JSON string containing the base64-encoded data and expiration timestamp.
 * @returns {Object|null} The decoded state object if valid and not expired, otherwise null.
 *
 * @throws Will log a warning and return null if decoding or parsing fails.
 */
export const fromBase64Payload = stored => {
  try {
    const { data, expiresAt } = JSON.parse(stored);
    if (Date.now() > expiresAt) {
      localStorage.removeItem(APP_STATE);
      return null;
    }

    return JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(data), c => c.charCodeAt(0))));
  } catch (err) {
    console.warn('Failed to decode persisted state:', err);
    return null;
  }
};
